import { useSyncExternalStore } from "react";
import { sessionInitiale, transition, type EffetNarration, type EvenementInfos, type SessionInfos } from "./logiqueInfos.js";

/**
 * ============================================================================
 *  etatInfos.ts — L'état du menu d'informations, partagé
 * ----------------------------------------------------------------------------
 *  Le bouton ⓘ / ⋯ vit à plusieurs endroits (groupe des langues, barre de
 *  navigation mobile, bandes du guide, barre de la main) ; le menu, la feuille
 *  et les crédits, eux, sont montés UNE fois (`InfoMenu`). L'état qui les relie
 *  est ici, sur le modèle de `etatNavigation` : un petit store à abonnement.
 *
 *  `origine` : le bouton qui a ouvert le menu. Il sert à ancrer le menu desktop
 *  sous lui, et à lui rendre le focus à la fermeture.
 *
 *  `rejeu` : un compteur. Le menu demande « Commandes de la visite », et
 *  `IndicateurRotation` rejoue l'indice à chaque incrément.
 * ==========================================================================*/

export interface EtatInfos {
  session: SessionInfos;
  origine: HTMLElement | null;
  /** Ouvert par un clic droit : le point (écran) où ancrer le menu, à la place du bouton. */
  ancre: { x: number; y: number } | null;
  rejeu: number;
}

export function creerEtatInfos() {
  let etat: EtatInfos = { session: sessionInitiale, origine: null, ancre: null, rejeu: 0 };
  const abonnes = new Set<() => void>();
  const poser = (suite: Partial<EtatInfos>) => {
    etat = { ...etat, ...suite };
    for (const abonne of abonnes) abonne();
  };

  return {
    lire: () => etat,
    abonner: (abonne: () => void) => {
      abonnes.add(abonne);
      return () => void abonnes.delete(abonne);
    },

    /** Applique un événement ; renvoie ce que la voix doit faire (à la charge de l'appelant). */
    appliquer(evenement: EvenementInfos, jouait: boolean, origine?: HTMLElement | null): EffetNarration {
      const { session, effet } = transition(etat.session, evenement, jouait);
      // Un bouton qui (ré)ouvre le menu reprend la main sur le clic droit : le menu s'ancre à lui.
      poser({ session, ...(origine !== undefined ? { origine, ancre: null } : null) });
      return effet;
    },
    /** Clic droit : le menu s'ancre au curseur ; pas de bouton à qui rendre le focus. */
    ancrer: (point: { x: number; y: number }) => poser({ ancre: point, origine: null }),
    demanderRejeu: () => poser({ rejeu: etat.rejeu + 1 }),
  };
}

export type StoreInfos = ReturnType<typeof creerEtatInfos>;

export const etatInfos = creerEtatInfos();

export function useEtatInfos<T>(choisir: (etat: EtatInfos) => T): T {
  return useSyncExternalStore(etatInfos.abonner, () => choisir(etatInfos.lire()));
}
